import { useCallback, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { TripCategory, type SortOptionEnum } from "@b2bportal/core-types";
import { useI18nContext } from "@hopper-b2b/i18n";
import { useChooseFare, useSelectDepartureFlight } from "@tenant/flights";
import { FlightSearchSelectors } from "../../../features/search/store";
import {
  getDestination,
  isInitializingValues,
} from "../../../features/search/store/selectors";
import {
  getFilteredAndSortedDepartureFlightList,
  getFlights,
  getSortOption,
} from "../../../features/shop/store/selectors";
import { setSortOption } from "../../../store";

export const useDeparturePageContainer = ({
  goToNextStep,
  onErrorReload,
}: {
  goToNextStep: () => void;
  onErrorReload: () => void;
}) => {
  const dispatch = useDispatch();
  const sortOption = useSelector(getSortOption);
  const handleSortChange = useCallback(
    (value: string) => {
      dispatch(setSortOption(value as SortOptionEnum));
    },
    [dispatch]
  );
  const { t } = useI18nContext();

  const [isAllCollapsed, setIsAllCollapsed] = useState<boolean>(false);

  const { isLoading, isError } = useSelector(getFlights);
  const isInitializing = useSelector(isInitializingValues);
  const flightList = useSelector(getFilteredAndSortedDepartureFlightList);

  const destination = useSelector(getDestination);
  const tripCategory = useSelector(FlightSearchSelectors.getTripCategory);

  const handleFareSelect = useSelectDepartureFlight();

  const handleChooseFare = useChooseFare(goToNextStep);

  const text = useMemo(() => {
    const subtitle = () => {
      switch (tripCategory) {
        case TripCategory.ONE_WAY:
          return t("core-flights.shopPage.subtitle.oneWay");
        case TripCategory.ROUND_TRIP:
          return t("core-flights.shopPage.subtitle.roundTrip");
      }
    };

    return {
      pageTitle: t("core-flights.departurePage.title.short"),
      subtitle: subtitle(),
    };
  }, [tripCategory, t]);

  return useMemo(() => {
    return {
      context: {
        isLoading,
        isError,
        isInitializing,
        flightList,
        destination,
        tripCategory,
        handleFareSelect,
        sortOption,
        isAllCollapsed,
      },
      handlers: {
        handleFareSelect,
        handleSortChange,
        handleChooseFare,
        onErrorReload,
        setIsAllCollapsed,
      },
      text,
    };
  }, [
    isLoading,
    isError,
    isInitializing,
    flightList,
    destination,
    tripCategory,
    handleFareSelect,
    sortOption,
    isAllCollapsed,
    handleSortChange,
    handleChooseFare,
    onErrorReload,
    text,
  ]);
};
